import { DayContentProps } from "react-day-picker";
import { Text } from "@/components/atoms/Text";
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from "@/components/molecules/Tooltip";
import { cn } from "@/lib/utils";

type AvailabilityBarProps = {
  isPartiallyBlocked: boolean;
  isSelectedDay?: boolean;
};

const dotStyles = {
  inquired: "tw-bg-yellow-600 tw-w-[4px] tw-h-[4px] tw-rounded-full",
  requested: "tw-bg-blue-700 tw-w-[4px] tw-h-[4px] tw-rounded-full",
  offerSubmitted: "tw-bg-red-800 tw-w-[4px] tw-h-[4px] tw-rounded-full",
  booking: "tw-bg-green-800 tw-w-[4px] tw-h-[4px] tw-rounded-full",
};

const AvailabilityBar = ({
  isPartiallyBlocked,
  isSelectedDay,
}: AvailabilityBarProps) => {
  return (
    <span
      className={cn(
        "tw-absolute tw-h-2 tw-w-16 tw-rounded-8 -tw-bottom-4 tw-bg-gray-600 tw-left-1/2 -tw-translate-x-1/2",
        {
          "tw-hidden": !isPartiallyBlocked,
          "tw-bg-gray-700": isSelectedDay,
        },
      )}
    >
      <span
        className={cn(
          "tw-absolute tw-inset-0 tw-bg-gray-400 tw-rounded-tl-8 tw-rounded-bl-8 tw-hidden tw-w-1/2",
          {
            "tw-inline-block": isPartiallyBlocked,
            "tw-bg-gray-600": isSelectedDay,
          },
        )}
      />
    </span>
  );
};

export const CalendarAvailabilityDayContent = ({
  date,
  activeModifiers,
}: DayContentProps) => {
  const isPartiallyBlocked =
    activeModifiers?.partiallyBooked &&
    !activeModifiers?.disabled &&
    !activeModifiers?.fullyBooked;

  const hasInquiredEvent = activeModifiers?.inquired;
  const hasRequestedEvent = activeModifiers?.requested;
  const hasOfferSubmittedEvent = activeModifiers?.offerSubmitted;
  const hasBookingEvent = activeModifiers?.booking;

  const Content = (
    <span
      className={cn("tw-size-full tw-flex tw-items-center tw-justify-center", {
        "!tw-text-gray-500 tw-line-through tw-pointer-events-none":
          activeModifiers?.fullyBooked,
      })}
    >
      <span className="tw-relative tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-1">
        {date.getDate()}
        <AvailabilityBar
          isPartiallyBlocked={isPartiallyBlocked}
          isSelectedDay={activeModifiers?.selected}
        />
        <div className="tw-flex tw-items-start tw-gap-2 tw-absolute tw-top-16">
          {hasBookingEvent && (
            <span
              key="booking-dot"
              className={dotStyles.booking}
              title="Booking"
            />
          )}
          {hasInquiredEvent && (
            <span
              key="inquired-dot"
              className={dotStyles.inquired}
              title="Inquiry"
            />
          )}
          {hasRequestedEvent && (
            <span
              key="requested-dot"
              className={dotStyles.requested}
              title="Request"
            />
          )}
          {hasOfferSubmittedEvent && (
            <span
              key="offer-submitted-dot"
              className={dotStyles.offerSubmitted}
              title="Offer"
            />
          )}
        </div>
      </span>
    </span>
  );

  return !isPartiallyBlocked ? (
    Content
  ) : (
    <Tooltip>
      <TooltipTrigger asChild>{Content}</TooltipTrigger>
      <TooltipContent side="bottom">
        <Text typeface="body-1" className="tw-text-gray-900">
          Partially booked
        </Text>
        <TooltipArrow className="tw-fill-base-white" />
      </TooltipContent>
    </Tooltip>
  );
};
